import React, { useState, useEffect } from 'react';
import { Folder, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { contentApi } from '@/services/contentApi';

interface NewItemDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (result: { type: string; item?: unknown; path?: string; contentType?: string }) => void;
  initialContentType?: string;
}

const CONTENT_TYPES = [
  { value: 'agent', label: 'Agent', folderPath: '/agents' },
  { value: 'skill', label: 'Skill', folderPath: '/skills' },
  { value: 'prompt', label: 'Prompt', folderPath: '/prompts' },
  { value: 'judge', label: 'Judge', folderPath: '/judges' },
];

const DEFAULT_TEMPLATES: Record<string, string> = {
  agent: `---
name: {{name}}
model: anthropic:claude-sonnet-4-20250514
temperature: 0.7
skills: []
---

# {{name}}

Describe what this agent does here.

## Instructions

Add your agent instructions here.
`,
  skill: `---
name: {{name}}
description: Description of this skill
---

# {{name}}

## Tools

Define the tools for this skill.
`,
  prompt: `---
name: {{name}}
---

# {{name}}

Your prompt content here.
`,
  judge: `---
name: {{name}}
model: anthropic:claude-sonnet-4-20250514
---

# {{name}}

## Evaluation Criteria

Define your evaluation criteria here.
`,
};

interface FolderOption {
  path: string;
  label: string;
  isRoot: boolean;
}

export function NewItemDialog({
  open,
  onClose,
  onCreated,
  initialContentType = 'agent',
}: NewItemDialogProps) {
  const [itemType, setItemType] = useState<'item' | 'folder'>('item');
  const [contentType, setContentType] = useState(initialContentType);
  const [name, setName] = useState('');
  const [parentFolder, setParentFolder] = useState<FolderOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableFolders, setAvailableFolders] = useState<FolderOption[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [folderPopoverOpen, setFolderPopoverOpen] = useState(false);

  useEffect(() => {
    if (open) {
      loadFolders();
    }
  }, [open, contentType]);

  const loadFolders = async () => {
    setFoldersLoading(true);
    try {
      const typeConfig = CONTENT_TYPES.find((t) => t.value === contentType);
      const rootPath = typeConfig?.folderPath || '/agents';

      const subfolders = await contentApi.listFolders(rootPath, contentType);

      const folderOptions: FolderOption[] = [
        { path: rootPath, label: rootPath, isRoot: true },
        ...subfolders.map((f: { path: string }) => ({ path: f.path, label: f.path, isRoot: false })),
      ];

      setAvailableFolders(folderOptions);
    } catch (err) {
      console.error('Failed to load folders:', err);
      const typeConfig = CONTENT_TYPES.find((t) => t.value === contentType);
      setAvailableFolders([
        { path: typeConfig?.folderPath || '/agents', label: typeConfig?.folderPath || '/agents', isRoot: true },
      ]);
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
      setError('Name can only contain letters, numbers, underscores, and hyphens');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const typeConfig = CONTENT_TYPES.find((t) => t.value === contentType);
      const basePath = parentFolder?.path || typeConfig?.folderPath || '/agents';

      if (itemType === 'folder') {
        const folderPath = `${basePath}/${name}`;
        await contentApi.createFolder(folderPath, contentType);
        onCreated({ type: 'folder', path: folderPath, contentType });
      } else {
        const template = DEFAULT_TEMPLATES[contentType] || DEFAULT_TEMPLATES.agent;
        const content = template.replace(/\{\{name\}\}/g, name);

        const item = await contentApi.createItem({
          name,
          content_type: contentType,
          folder_path: basePath,
          content,  // createItem handles base64 encoding
          publish: true,
        });

        onCreated({ type: 'item', item });
      }

      setName('');
      setParentFolder(null);
      setError(null);
      onClose();
    } catch (err) {
      console.error('Failed to create:', err);
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setParentFolder(null);
    setError(null);
    onClose();
  };

  const typeConfig = CONTENT_TYPES.find((t) => t.value === contentType);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            Create New {itemType === 'folder' ? 'Folder' : 'Item'}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Item Type Toggle */}
          <div className="space-y-2">
            <Label>Type</Label>
            <ToggleGroup
              type="single"
              value={itemType}
              onValueChange={(value) => value && setItemType(value as 'item' | 'folder')}
              className="justify-start"
            >
              <ToggleGroupItem value="item" className="flex-1">
                <FileText className="h-4 w-4 mr-2" />
                Item
              </ToggleGroupItem>
              <ToggleGroupItem value="folder" className="flex-1">
                <Folder className="h-4 w-4 mr-2" />
                Folder
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* Content Type Select */}
          <div className="space-y-2">
            <Label>Content Type</Label>
            <Select value={contentType} onValueChange={setContentType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Name Input */}
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={itemType === 'folder' ? 'my_folder' : 'my_agent'}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Use letters, numbers, underscores, or hyphens
            </p>
          </div>

          {/* Parent Folder */}
          <div className="space-y-2">
            <Label>Parent Folder</Label>
            <Popover open={folderPopoverOpen} onOpenChange={setFolderPopoverOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start">
                  <Folder className="h-4 w-4 mr-2" />
                  {parentFolder?.path || typeConfig?.folderPath || 'Select folder...'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0">
                <Command>
                  <CommandInput placeholder="Search folders..." />
                  <CommandList>
                    <CommandEmpty>No folders found.</CommandEmpty>
                    <CommandGroup>
                      {availableFolders.map((folder) => (
                        <CommandItem
                          key={folder.path}
                          value={folder.path}
                          onSelect={() => {
                            setParentFolder(folder);
                            setFolderPopoverOpen(false);
                          }}
                        >
                          <Folder className="h-4 w-4 mr-2" />
                          {folder.path}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">
              Default: {typeConfig?.folderPath || '/agents'}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading || !name.trim()}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {loading ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default NewItemDialog;
